import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, UserPlus, Buildings, Briefcase, User, AddressBook, HardHat } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import ContactSearch from '../ui/ContactSearch';
import InvitationLinkDialog from '../shared/InvitationLinkDialog';
import { SPECIALITES_PARTENAIRE, libelleRole } from '../../lib/libelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * B1 (B16, B14, P1#1, P1#5-8) — une seule fenêtre « Ajouter quelqu'un au chantier » :
 * membre du cabinet, client, AMO, partenaire (BET…), entreprise, contact sans accès.
 * Les choix sont les uns sous les autres (plus de boutons coupés à droite) ; le client
 * n'est invité que si la case « Inviter le client » est cochée (décochée en prospection),
 * et chaque invitation dont l'e-mail n'est pas parti affiche le lien à transmettre.
 */

export const MODES_AJOUT = [
  { cle: 'cabinet', libelle: 'Membre du cabinet', aide: 'Associé, chef de projet, collaborateur', icon: Buildings },
  { cle: 'client', libelle: "Client (maître d'ouvrage)", aide: 'Propriétaire, syndic, copropriétaire', icon: User },
  { cle: 'amo', libelle: 'AMO', aide: "Assistant à maîtrise d'ouvrage, côté client", icon: Briefcase },
  { cle: 'partenaire', libelle: 'Partenaire (BET, contrôleur technique, SPS, OPC…)', aide: 'Accès au projet par invitation', icon: UserPlus },
  { cle: 'entreprise', libelle: 'Entreprise', aide: "Entreprise de travaux (accès à l'app terrain)", icon: HardHat },
  { cle: 'externe', libelle: 'Contact sans accès', aide: "Dans l'annuaire du chantier, sans compte", icon: AddressBook },
];

// Fonction par défaut dans l'équipe selon le compte (P1#8 : plus de « Autre » pour un collaborateur).
export const FONCTION_PAR_ROLE = {
  admin: 'Architecte',
  architect: 'Architecte',
  moe_principal: 'Chef de projet',
  collaborateur: 'Collaborateur',
  moe_membre: 'Collaborateur',
};
const ROLES_CABINET = Object.keys(FONCTION_PAR_ROLE);

export const FONCTIONS_EQUIPE = [
  'Architecte', 'Chef de projet', 'Collaborateur', 'AMO', 'BET Structure', 'BET Fluides', 'SPS',
  'Économiste', 'OPC', 'Géomètre', 'Contrôleur technique', 'Entreprise', 'Autre',
];

const VIDE = { prenom: '', nom: '', email: '', telephone: '', societe: '', specialite: '', mission: '', fonction: 'Autre' };

export default function AjouterAuChantierDialog({ open, onClose, chantierId, modeInitial = 'cabinet', dejaAjoutes = new Set(), prospection = false, onAdded }) {
  const [mode, setMode] = useState(modeInitial);
  const [form, setForm] = useState(VIDE);
  const [inviterClient, setInviterClient] = useState(!prospection);
  const [cabinet, setCabinet] = useState([]);
  const [envoi, setEnvoi] = useState(false);
  const [lien, setLien] = useState(null);

  useEffect(() => { if (open) { setMode(modeInitial); setForm(VIDE); setInviterClient(!prospection); } }, [open, modeInitial, prospection]);
  useEffect(() => {
    if (!open) return;
    axios.get(`${API_URL}/api/users/intervenants`, { withCredentials: true })
      .then(({ data }) => setCabinet((Array.isArray(data) ? data : []).filter((u) => ROLES_CABINET.includes(u.role))))
      .catch(() => setCabinet([]));
  }, [open]);

  const fin = () => { onAdded?.(); onClose(); };
  const erreur = (e, defaut) => {
    const d = e.response?.data?.detail;
    toast.error(typeof d === 'string' ? d : defaut);
  };
  // Même retour pour tout le monde : e-mail parti, ou lien à transmettre (RP08).
  const retourInvitation = (data, nom, email) => {
    if (data?.email_sent) {
      toast.success(`Invitation envoyée à ${email}`);
    } else if (data?.activation_url) {
      toast.warning("Accès créé, mais l'e-mail n'est pas parti");
      setLien({ ...data, user_name: nom, user_email: email });
      return true;
    } else if (data?.invite || data?.created === false) {
      toast.warning(`${email} a accès au chantier. L'e-mail n'est pas parti : prévenez-le qu'il se connecte avec son adresse habituelle.`);
    } else {
      toast.success('Ajouté au chantier');
    }
    return false;
  };

  const ajouterCabinet = async (u) => {
    try {
      await axios.post(`${API_URL}/api/projets/${chantierId}/moe`, {
        name: u.name, email: u.email, telephone: u.phone || '', role: FONCTION_PAR_ROLE[u.role] || 'Collaborateur', user_id: u._id,
      }, { withCredentials: true });
      toast.success(`${u.name} ajouté à l'équipe`);
      onAdded?.();
    } catch (e) { erreur(e, 'Ajout impossible'); }
  };

  const ajouterClient = async () => {
    setEnvoi(true);
    try {
      const email = form.email.trim();
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/clients`, {
        nom: [form.prenom, form.nom].filter(Boolean).join(' ').trim(), email, telephone: form.telephone.trim(),
        inviter: !!(inviterClient && email),
      }, { withCredentials: true });
      let lienAffiche = false;
      if (data?.invite) lienAffiche = retourInvitation(data, form.nom, email);
      else toast.success('Client ajouté (sans accès au chantier)');
      if (!lienAffiche) fin(); else onAdded?.();
    } catch (e) { erreur(e, 'Ajout du client impossible'); } finally { setEnvoi(false); }
  };

  const inviterExterne = async (role) => {
    setEnvoi(true);
    try {
      const email = form.email.trim();
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/intervenants`, {
        email, nom: form.nom.trim(), prenom: form.prenom.trim() || null, role,
        telephone: form.telephone.trim() || null, societe: form.societe.trim() || null,
        specialite: role === 'partenaire' ? (form.specialite || null) : null,
      }, { withCredentials: true });
      // Entreprise : fiche dans l'équipe du chantier (le compte est déjà inscrit au chantier)
      if (role === 'entreprise' && data?.user_id) {
        await axios.post(`${API_URL}/api/projets/${chantierId}/moe`, {
          name: form.societe.trim() || form.nom.trim(), email, telephone: form.telephone.trim(),
          role: 'Entreprise', mission: form.mission.trim(), user_id: data.user_id, role_projet: 'entreprise',
        }, { withCredentials: true }).catch(() => {});
      }
      const lienAffiche = retourInvitation(data, form.nom, email);
      if (!lienAffiche) fin(); else onAdded?.();
    } catch (e) { erreur(e, 'Invitation impossible'); } finally { setEnvoi(false); }
  };

  const ajouterExterne = async () => {
    setEnvoi(true);
    try {
      await axios.post(`${API_URL}/api/projets/${chantierId}/moe`, {
        name: [form.prenom, form.nom].filter(Boolean).join(' ').trim(), email: form.email.trim(),
        telephone: form.telephone.trim(), role: form.fonction, mission: form.mission.trim(),
      }, { withCredentials: true });
      toast.success('Contact ajouté');
      fin();
    } catch (e) { erreur(e, 'Ajout impossible'); } finally { setEnvoi(false); }
  };

  const champ = (cle, label, props = {}) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input value={form[cle]} onChange={(e) => setForm({ ...form, [cle]: e.target.value })} className="rounded-none mt-1" {...props} />
    </div>
  );
  const identite = (emailObligatoire) => (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {champ('prenom', 'Prénom')}
        {champ('nom', 'Nom *', { 'data-testid': 'ajout-nom' })}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {champ('email', emailObligatoire ? 'E-mail *' : 'E-mail', { type: 'email', 'data-testid': 'ajout-email' })}
        {champ('telephone', 'Téléphone')}
      </div>
    </>
  );
  const cabinetDispo = cabinet.filter((u) => !dejaAjoutes.has(u._id));

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-xl max-h-[88vh] overflow-y-auto" data-testid="ajouter-au-chantier-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Ajouter quelqu'un au chantier</DialogTitle>
          </DialogHeader>

          {/* B16 — les choix les uns sous les autres (plus rien de coupé à droite) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label="Qui ajoutez-vous ?">
            {MODES_AJOUT.map(({ cle, libelle, aide, icon: Icon }) => (
              <button
                key={cle}
                type="button"
                role="radio"
                aria-checked={mode === cle}
                onClick={() => { setMode(cle); setForm(VIDE); }}
                className={`text-left p-2.5 border flex items-start gap-2 min-h-[56px] ${mode === cle ? 'border-[#2C2C2C] bg-[#F5F5F3]' : 'border-[#E0E0DB] hover:border-[#9A9A9A]'}`}
                data-testid={`ajout-mode-${cle}`}
              >
                <Icon size={18} className="mt-0.5 flex-shrink-0 text-[#C75B39]" />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-[#2C2C2C] leading-tight">{libelle}</span>
                  <span className="block text-[11px] text-[#6B6B6B] leading-tight mt-0.5">{aide}</span>
                </span>
              </button>
            ))}
          </div>

          <div className="border-t border-[#E0E0DB] pt-4 space-y-3">
            {mode === 'cabinet' && (
              cabinetDispo.length === 0 ? (
                <p className="text-sm text-[#9A9A9A] text-center py-4">Tous les membres du cabinet sont déjà dans l'équipe.</p>
              ) : (
                <div className="space-y-2" data-testid="ajout-cabinet-liste">
                  {cabinetDispo.map((u) => (
                    <div key={u._id} className="flex items-center gap-3 p-2 border border-[#E0E0DB]" data-testid={`cabinet-user-${u._id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{u.name}</p>
                        <p className="text-xs text-[#9A9A9A] truncate">{libelleRole(u.role)} · {u.email}</p>
                      </div>
                      <Button size="sm" className="rounded-none gap-1 text-xs bg-[#2C2C2C] hover:bg-[#4A4A4A] flex-shrink-0"
                        onClick={() => ajouterCabinet(u)} data-testid={`add-cabinet-user-${u._id}`}>
                        <Plus size={14} /> Ajouter
                      </Button>
                    </div>
                  ))}
                </div>
              )
            )}

            {mode === 'client' && (
              <div className="space-y-3" data-testid="ajout-client-form">
                <div>
                  <Label className="text-xs text-[#6B6B6B]">Client déjà connu ?</Label>
                  <ContactSearch
                    sources={['users']}
                    userRoleFilter="client"
                    placeholder="Tapez un nom ou un e-mail…"
                    onSelect={(c) => { if (c) setForm({ ...form, prenom: '', nom: c.name || '', email: c.email || '', telephone: c.telephone || '' }); }}
                    onCreateNew={(q) => setForm({ ...form, nom: q })}
                    testId="ajout-client-search"
                  />
                </div>
                {identite(false)}
                <label className={`flex items-start gap-2 text-sm ${form.email.trim() ? '' : 'text-[#9A9A9A]'}`}>
                  <input type="checkbox" className="mt-1" checked={inviterClient && !!form.email.trim()} disabled={!form.email.trim()}
                    onChange={(e) => setInviterClient(e.target.checked)} data-testid="ajout-inviter-client" />
                  <span>
                    Inviter le client à suivre le chantier
                    <span className="block text-xs text-[#6B6B6B]">
                      Sans invitation, le client figure dans l'équipe mais n'a pas accès au chantier
                      {prospection ? ' (chantier en prospection : case décochée par défaut).' : '.'}
                    </span>
                  </span>
                </label>
                <Button className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none w-full" onClick={ajouterClient}
                  disabled={envoi || !form.nom.trim()} data-testid="ajout-client-submit">
                  {envoi ? 'Envoi…' : (inviterClient && form.email.trim() ? 'Ajouter et inviter' : 'Ajouter le client')}
                </Button>
              </div>
            )}

            {(mode === 'amo' || mode === 'partenaire') && (
              <div className="space-y-3" data-testid="equipe-invitation-form">
                <p className="text-xs text-[#6B6B6B]">La personne reçoit un e-mail avec un lien pour accéder au projet. Aucun mot de passe n'est envoyé.</p>
                {identite(true)}
                {champ('societe', 'Société')}
                {mode === 'partenaire' && (
                  <div>
                    <Label className="text-xs">Spécialité (facultatif)</Label>
                    <Select value={form.specialite || 'aucune'} onValueChange={(v) => setForm({ ...form, specialite: v === 'aucune' ? '' : v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="equipe-invitation-specialite"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aucune">Non précisée</SelectItem>
                        {Object.entries(SPECIALITES_PARTENAIRE).map(([code, lib]) => <SelectItem key={code} value={code}>{lib}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none w-full" onClick={() => inviterExterne(mode)}
                  disabled={envoi || !form.nom.trim() || !form.email.trim()} data-testid="equipe-invitation-submit">
                  {envoi ? 'Envoi…' : 'Inviter'}
                </Button>
              </div>
            )}

            {mode === 'entreprise' && (
              <div className="space-y-3" data-testid="ajout-entreprise-form">
                <div>
                  <Label className="text-xs text-[#6B6B6B]">Entreprise de l'annuaire ?</Label>
                  <ContactSearch
                    sources={['entreprises']}
                    placeholder="Nom de l'entreprise…"
                    onSelect={(c) => { if (c) setForm({ ...form, societe: c.name || '', email: c.email || form.email, telephone: c.telephone || form.telephone }); }}
                    testId="ajout-entreprise-search"
                  />
                </div>
                {champ('societe', 'Entreprise *')}
                {identite(true)}
                {champ('mission', 'Lot ou mission', { placeholder: 'Ex : Lot 03 — Peinture' })}
                <Button className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none w-full" onClick={() => inviterExterne('entreprise')}
                  disabled={envoi || !form.nom.trim() || !form.email.trim() || !form.societe.trim()} data-testid="ajout-entreprise-submit">
                  {envoi ? 'Envoi…' : "Inviter l'entreprise"}
                </Button>
              </div>
            )}

            {mode === 'externe' && (
              <div className="space-y-3" data-testid="ajout-externe-form">
                {identite(false)}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Fonction</Label>
                    <Select value={form.fonction} onValueChange={(v) => setForm({ ...form, fonction: v })}>
                      <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FONCTIONS_EQUIPE.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {champ('mission', 'Mission', { placeholder: 'Ex : géomètre' })}
                </div>
                <Button className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none w-full" onClick={ajouterExterne}
                  disabled={envoi || !form.nom.trim()} data-testid="ajout-externe-submit">
                  Ajouter
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <InvitationLinkDialog invitation={lien} onClose={() => { setLien(null); onClose(); }} />
    </>
  );
}
